import EventEmitter from "common/utils/EventEmitter.js";
import LiveStreaming from "./LiveStreaming";
import VideoStateManager from "./VideoStateManager";

import { USER_ROLE } from "./enums/LiveStreamingEnums";
import LoggerService from "./LoggerService";
import PeerConnectionManager from "./PeerConnectionManager";

export default class LiveStreamingTool {
  static config = {};

  /**
   * this method will listen to event being and triggers the listener function
   *
   * @static
   * @param {String} eventName - takes event name
   * @param {Function} listener - takes a function as callback listener
   *
   */
  static on(eventName, listener) {
    EventEmitter.on(eventName, listener);
  }

  /**
   * this method will remove the listener functions that were bind to the event
   *
   * @static
   * @param {String} eventName - takes event name
   * @param {Function} listener - takes reference to the listener function
   *
   */
  static off(eventName, listener) {
    EventEmitter.off(eventName, listener);
  }

  /**
   * this method will initialize live streaming
   *
   * it also initializes few classes based on user role,
   * and enables logging based on configuration provided
   *
   * @param {Object} config - configuration object
   * @param {Object} callbacks - object of callback methods
   * @example
   * let config = {};
   * config.userId = userGUID;
   * config.userRole = 1;
   * config.authType = 1;
   * config.websocketConnectionURL = LIVE_STREAMING_API_HUB_URL;
   * config.basePath = LIVE_STREAMING_BASE_PATH;
   *
   * let callbacks = {};
   * callbacks.getTurnServerConfig = getTurnServerConfig;
   * callbacks.getLiveStreamingWebSocketToken = getLiveStreamingWebSocketToken;
   *
   * await window.TestMaverick.LiveStreamingTool.init(config, callbacks);
   *
   */
  static async init(config, callbacks) {
    LiveStreaming.init(config, callbacks);
    LiveStreamingTool.config = { ...config };

    if (LiveStreaming.config.userRole == USER_ROLE.VIEWER) {
      VideoStateManager.init();
    } else {
      PeerConnectionManager.listenToPeerConnectionEvents();
    }
  }

  /**
   * starts live streaming
   * @static
   * @returns {Promise<void>}
   * @example
   * await LiveStreamingTool.start();
   */
  static async start() {
    await LiveStreaming.start();
  }

  /**
   * stops live streaming
   * @static
   * @returns {Promise<void>}
   * @example
   * await LiveStreamingTool.stop();
   */
  static async stop() {
    await LiveStreaming.stop();
  }

  /**
   * takes webcam media stream as input and uses it for streaming
   * @param {MediaStream} stream
   * @returns {Promise<void>}
   * @example
   * await LiveStreamingTool.addWebcamStream(webcamStream);
   */
  static addWebcamStream(stream) {
    LiveStreaming.addWebcamStream(stream);
  }

  /**
   * takes screenshare stream as input and uses it for streaming
   * @param {MediaStream} stream
   * @returns {Promise<void>}
   * @example
   * await LiveStreamingTool.addScreenshareStream(screenshareStream);
   */
  static addScreenshareStream(stream) {
    LiveStreaming.addScreenshareStream(stream);
  }

  /**
   * removes webcam stream and stops all peer connections in which webcam stream was being used for streaming
   * @returns {Promise<void>}
   * @example
   * await LiveStreamingTool.removeWebcamStream();
   */
  static removeWebcamStream() {
    LiveStreaming.removeWebcamStream();
  }

  /**
   * removes screenshare stream and stops all peer connections in which screenshare stream was being used for streaming
   * @returns {Promise<void>}
   *
   * @example
   * await LiveStreamingTool.removeScreenshareStream();
   */
  static removeScreenshareStream() {
    LiveStreaming.removeScreenshareStream();
  }

  /**
   * Sends a media start request for the specified user with the given streams.
   *
   * @static
   * @async
   * @param {string} userId - The unique identifier of the user requesting for the media streaming.
   * @param {Object} requestedStreams - An object of boolean configuration for streams
   * @returns {Promise<void>} Resolves when the media start request is successfully sent.
   *
   * @example
   * await LiveStreamingTool.sendMediaStartRequest(userId, {webcam: true,screen: true});
   */
  static async sendMediaStartRequest(userId, requestedStreams) {
    await LiveStreaming.sendMediaStartRequest(userId, requestedStreams);
  }

  /**
   * Sends a media start request for the specified user with the given streams.
   *
   * @static
   * @async
   * @param {string} peerId - The unique identifier of the peer whose connection is to be stopped.
   * @returns {Promise<void>} Resolves when the media start request is successfully sent.
   *
   * @example
   * await LiveStreamingTool.sendMediaStopRequest(peerId);
   */
  static async sendMediaStopRequest(peerId) {
    await LiveStreaming.sendMediaStopRequest(peerId);
  }

  /**
   * Retrieves the webcam stream for a specific peer.
   *
   * @static
   * @function getRemoteStream
   * @param {string} peerId - The unique identifier of the peer whose webcam stream is to be retrieved.
   * @returns {MediaStream|null} The webcam stream of the specified peer, or `null` if no stream is available.
   *
   * @example
   * const webcamStream = LiveStreamingTool.getRemoteStream('peer123');
   * if (webcamStream) {
   *   console.log('stream retrieved:', stream);
   * } else {
   *   console.log('No webcam stream available for the specified peer.');
   * }
   */
  static getRemoteStream(peerId) {
    return LiveStreaming.getRemoteStream(peerId);
  }

  /**
   * Retrieves the local user's webcam stream.
   *
   * @static
   * @function getLocalWebcamStream
   * @returns {MediaStream|null} The local user's webcam stream, or `null` if no stream is available.
   *
   * @example
   * // Usage example
   * const localStream = LiveStreamingTool.getLocalWebcamStream();
   * if (localStream) {
   *   console.log('Local webcam stream retrieved:', localStream);
   * } else {
   *   console.log('No local webcam stream available.');
   * }
   */
  static getLocalWebcamStream() {
    return LiveStreaming.getLocalWebcamStream();
  }

  /**
   * Retrieves the local user's screenshare stream.
   *
   * @static
   * @function getLocalScreenshareStream
   * @returns {MediaStream|null} The local user's screenshare stream, or `null` if no stream is available.
   *
   * @example
   * // Usage example
   * const localStream = LiveStreamingTool.getLocalscreenshareStream();
   * if (localStream) {
   *   console.log('Local screenshare stream retrieved:', localStream);
   * } else {
   *   console.log('No local screenshare stream available.');
   * }
   */
  static getLocalScreenshareStream() {
    return LiveStreaming.getLocalScreenshareStream();
  }

  static getLiveStreamingMapping() {
    return LiveStreaming.getLiveStreamingMapping();
  }

  static getActiveRequests() {
    return LiveStreaming.getActiveRequests();
  }

  static getConnectionState(peerId) {
    return LiveStreaming.getConnectionState(peerId);
  }

  static getPeerId(userId, mediaType) {
    return LiveStreaming.getPeerId(userId, mediaType);
  }

  static getDataFromPeerId(peerId) {
    return LiveStreaming.getDataFromPeerId(peerId);
  }

  static getVideoId(peerId, mediaType) {
    return VideoStateManager.getVideoId(peerId, mediaType);
  }

  static enableLogging(config) {
    LoggerService.enableLogging(config);
  }

  static disableLogging() {
    LoggerService.disableLogging();
  }

  static async reset() {
    await LiveStreaming.reset();
  }

  static notifyActiveConnections() {
    LiveStreaming.notifyActiveConnections();
  }
}

if (window.TestMaverick === undefined) {
  window.TestMaverick = {};
}

window.TestMaverick.LiveStreamingTool = LiveStreamingTool;
